import type { ReactNode } from 'react';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import type { Confidence } from '../../lib/initial-form';

interface FieldProps {
  label: string;
  confidence: Confidence;
  children: ReactNode;
  htmlFor?: string;
}

export function Field({ label, confidence, children, htmlFor }: FieldProps) {
  return (
    <label htmlFor={htmlFor} className="flex flex-col gap-1 text-sm">
      <span className="flex items-center justify-between text-slate-700">
        <span className="font-medium">{label}</span>
        <ConfidenceIndicator level={confidence} />
      </span>
      {children}
    </label>
  );
}

interface BaseInputProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'date' | 'number';
  placeholder?: string;
  disabled?: boolean;
}

export function TextInput({
  id,
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled,
}: BaseInputProps) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
    />
  );
}

interface SelectProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[] | ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string;
}

export function Select({ id, value, onChange, options, placeholder }: SelectProps) {
  const normalized = normalize(options);
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="">{placeholder ?? '- Choisir -'}</option>
      {normalized.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function normalize(
  opts: readonly string[] | ReadonlyArray<{ value: string; label: string }>,
): Array<{ value: string; label: string }> {
  return opts.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
}
